import type { TranscriptEntry } from "../hooks/useGladiaTranscription";
import type { PersistedTranscriptEntry } from "../services/transcripts";

interface ExportTranscriptEntry {
  text: string;
  timestamp: number;
  seq?: number;
  url?: string | null;
}

function fallbackIdentity(entry: ExportTranscriptEntry): string {
  return `${entry.timestamp}:${entry.text}`;
}

function mergeExportEntries(
  history: PersistedTranscriptEntry[],
  memory: TranscriptEntry[],
): ExportTranscriptEntry[] {
  const merged: ExportTranscriptEntry[] = [];
  const bySequence = new Map<number, number>();
  const byTimestampAndText = new Map<string, number>();

  const append = (entry: ExportTranscriptEntry) => {
    const fallback = fallbackIdentity(entry);
    const existing =
      (entry.seq !== undefined ? bySequence.get(entry.seq) : undefined) ??
      byTimestampAndText.get(fallback);
    if (existing !== undefined) {
      return;
    }

    const index = merged.length;
    merged.push(entry);
    if (entry.seq !== undefined) {
      bySequence.set(entry.seq, index);
    }
    byTimestampAndText.set(fallback, index);
  };

  for (const entry of history) {
    if (!entry.is_final || !entry.text.trim()) continue;
    append({
      text: entry.text.trim(),
      timestamp: entry.ts_ms,
      seq: entry.seq,
      url: entry.url,
    });
  }
  for (const entry of memory) {
    if (!entry.isFinal || !entry.text.trim()) continue;
    append({
      text: entry.text.trim(),
      timestamp: entry.timestamp,
      seq: entry.seq,
      url: entry.url,
    });
  }

  return merged.sort((left, right) => left.timestamp - right.timestamp);
}

function formatLocalTime(timestamp: number): string {
  const date = new Date(timestamp);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** Build a stable plain-text export from persisted and not-yet-flushed entries. */
export function buildTranscriptExport(
  history: PersistedTranscriptEntry[],
  memory: TranscriptEntry[],
): string {
  const lines = mergeExportEntries(history, memory).map((entry) => {
    const url = entry.url ? ` — URL: ${entry.url}` : "";
    return `[${formatLocalTime(entry.timestamp)}] ${entry.text}${url}`;
  });
  return lines.length > 0 ? `${lines.join("\n")}\n` : "";
}

export function buildTranscriptFilename(
  sessionId: string,
  now: Date = new Date(),
): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  const safeSessionId = sessionId.replace(/[^a-zA-Z0-9_-]/g, "-") || "session";
  const timestamp = [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    "-",
    pad(now.getHours()),
    pad(now.getMinutes()),
  ].join("");
  return `transcript-${safeSessionId}-${timestamp}.txt`;
}

